// src/router/ProtectedRoute.jsx
//
// Relocated from src/components/ProtectedRoute.jsx (folder-structure cleanup —
// this is router-guard logic, not a UI component, so it belongs next to
// AppRoutes.jsx which is its only consumer). Ported onto AuthContext's
// useAuth() instead of utils/session — permissions are resolved from
// GET /auth/me (see AuthContext.jsx's own docstring), not decoded from
// the JWT.
import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import RestrictedAccess from "./RestrictedAccess";

function FullPageLoader() {
  return (
    <div className="flex items-center justify-center w-screen h-screen bg-white">
      <Loader2 size={32} className="animate-spin text-blue-500" />
    </div>
  );
}

export default function ProtectedRoute({ requiredPermissions = [], children }) {
  const location = useLocation();
  const { isAuthenticated, permissionsLoading, hasPermission } = useAuth();
  const { permissionsSynced } = useWorkspace();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permissions are fetched (GET /auth/me) and then, separately, resolved
  // against whichever project is currently selected (WorkspaceContext) —
  // hold off judging requiredPermissions until BOTH have actually settled,
  // so a page refresh/deep link doesn't bounce to /unauthorized during the
  // window before either has anything real to check against. Rendering a
  // visible loader here (not null) matters because the wrong outcome of
  // this check is a one-shot <Navigate> — correcting the underlying
  // permissions state one render later can't undo an already-committed
  // redirect the way a passively re-rendered sidebar link self-heals.
  if (requiredPermissions.length && (permissionsLoading || !permissionsSynced)) {
    return <FullPageLoader />;
  }

  if (
    requiredPermissions.length &&
    !requiredPermissions.every((p) => hasPermission(p))
  ) {
    // RestrictedAccess, not <Navigate> -- a plain conditional render, so
    // switching to an account/project that does grant the permission
    // flips straight back to `children` on the next render. A <Navigate>
    // is one-shot: once it fires the browser has already left the page,
    // and a permission becoming valid again a render later can't undo an
    // already-committed redirect.
    return <RestrictedAccess />;
  }

  return children;
}
